/**
 * The skin registry.
 *
 * A skin is exactly two components: the screen before a session exists, and the
 * screen after one does. `App` owns which of the two is showing — that is
 * routing, and it is the same for both skins — so a skin never decides when it
 * is mounted, only what it looks like.
 *
 * Every skin is imported statically. They are pure CSS and markup with no
 * images, so shipping the unused ones costs a few kilobytes; lazy-loading would
 * buy a Suspense boundary and a flash of nothing in exchange for that.
 *
 * The dependency runs one way: skins import from `src/lib/`, and `src/lib/`
 * must never import from here. It is the same rule `shared/contracts.ts` states
 * about `api/` and `frontend/` — the shared floor stays a floor.
 */

import type { ComponentType } from "react";
import type { SkinName } from "../lib/skin.js";
import { BuildView as ClassicBuildView } from "./classic/BuildView.js";
import { PromptView as ClassicPromptView } from "./classic/PromptView.js";
import { BuildView as HatchBuildView } from "./hatch/BuildView.js";
import { PromptView as HatchPromptView } from "./hatch/PromptView.js";
import { BuildView as XanoBuildView } from "./xano/BuildView.js";
import { PromptView as XanoPromptView } from "./xano/PromptView.js";

export interface Skin {
  /** No session yet: the prompt box and the gallery. */
  PromptView: ComponentType<{ onStarted: (sessionId: string) => void }>;
  /** A session exists: everything from first activity to the live site. */
  BuildView: ComponentType<{ sessionId: string; onRestart: () => void }>;
}

export const SKINS: Record<SkinName, Skin> = {
  classic: { PromptView: ClassicPromptView, BuildView: ClassicBuildView },
  hatch: { PromptView: HatchPromptView, BuildView: HatchBuildView },
  xano: { PromptView: XanoPromptView, BuildView: XanoBuildView },
};
